/**
 * A single-file dropzone for artifact uploads (f15.1). It inspects NOTHING
 * about the file: the server sniffs the bytes and decides PDF or HTML, so a
 * report named `.pdf` that is really HTML is accepted as HTML.
 */

import { useRef, useState } from 'react';
import { FileCheck2, UploadCloud } from 'lucide-react';

import { formatSize } from './artifactView';

export function FileDropzone({
  file,
  onFile,
  disabled = false,
  prompt = 'Drop a PDF or HTML report here',
}: {
  file: File | null;
  onFile: (file: File) => void;
  disabled?: boolean;
  prompt?: string;
}) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  return (
    <div
      className={`dropzone${over ? ' is-over' : ''}${file ? ' is-filled' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const dropped = event.dataTransfer.files.item(0);
        if (dropped && !disabled) onFile(dropped);
      }}
    >
      {file ? (
        <>
          <FileCheck2 className="dz-icon" aria-hidden="true" />
          <div className="dz-title">{file.name}</div>
          <div className="hint">{formatSize(file.size)} · the format is checked when you upload</div>
        </>
      ) : (
        <>
          <UploadCloud className="dz-icon" aria-hidden="true" />
          <div className="dz-title">{prompt}</div>
          <div className="hint">or choose a file · format is detected by content, not by its name</div>
        </>
      )}
      <div className="btn-row dz-actions">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={disabled}
          onClick={() => input.current?.click()}
        >
          {file ? 'Choose another file' : 'Choose file'}
        </button>
      </div>
      <input
        ref={input}
        type="file"
        className="sr-only"
        aria-label="Artifact file"
        disabled={disabled}
        onChange={(event) => {
          const chosen = event.target.files?.item(0);
          if (chosen) onFile(chosen);
          event.target.value = '';
        }}
      />
    </div>
  );
}
